import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const GlobeAnimation = lazy(() => import('../../../components/ui/GlobeAnimation'));
const FloatingCards = lazy(() => import('../../../components/ui/FloatingCards'));

/**
 * TrendingConnectionsFeed — shows real recent executive positions,
 * investments, and PAC connections in a live-feed style,
 * with the globe animation as a background.
 */
const TrendingConnectionsFeed = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        // Fetch recent exec positions with person + company names
        const { data: execData } = await supabase
          .from('executives')
          .select(`
            id, position, start_date,
            user_profiles ( id, full_name ),
            companies ( id, name, industry )
          `)
          .order('created_at', { ascending: false })
          .limit(10);

        // Fetch recent investments with person + company names
        const { data: investData } = await supabase
          .from('investments')
          .select(`
            id, investment_type, investment_amount,
            user_profiles ( id, full_name ),
            companies ( id, name, industry )
          `)
          .order('created_at', { ascending: false })
          .limit(10);

        // Merge and tag
        const feed = [];

        (execData || []).forEach((ep) => {
          if (ep.user_profiles && ep.companies) {
            feed.push({
              id: `exec-${ep.id}`,
              type: 'executive',
              personName: ep.user_profiles.full_name,
              personId: ep.user_profiles.id,
              companyName: ep.companies.name,
              companyId: ep.companies.id,
              detail: ep.position || 'Executive',
              industry: ep.companies.industry,
              icon: 'Briefcase',
              color: 'blue',
            });
          }
        });

        (investData || []).forEach((inv) => {
          if (inv.user_profiles && inv.companies) {
            const amount = inv.investment_amount;
            let amountStr = '';
            if (amount >= 1_000_000_000) amountStr = `$${(amount / 1_000_000_000).toFixed(1)}B`;
            else if (amount >= 1_000_000) amountStr = `$${(amount / 1_000_000).toFixed(0)}M`;
            else if (amount > 0) amountStr = `$${amount.toLocaleString()}`;

            feed.push({
              id: `invest-${inv.id}`,
              type: 'investment',
              personName: inv.user_profiles.full_name,
              personId: inv.user_profiles.id,
              companyName: inv.companies.name,
              companyId: inv.companies.id,
              detail: `${inv.investment_type || 'Investor'}${amountStr ? ` · ${amountStr}` : ''}`,
              industry: inv.companies.industry,
              icon: 'TrendingUp',
              color: 'emerald',
            });
          }
        });

        // Shuffle to make it feel dynamic, then take first 12
        const shuffled = feed.sort(() => Math.random() - 0.5).slice(0, 12);
        setConnections(shuffled);
      } catch (err) {
        console.error('Trending connections error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConnections();
  }, []);

  if (loading) return null;
  if (connections.length === 0) return null;

  const colorMap = {
    blue: 'bg-blue-500/20 text-blue-300 border-blue-400/30',
    emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  };

  return (
    <section className="relative py-14 px-4 overflow-hidden">
      {/* Globe animation + floating cards background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#020a18] via-[#051225] to-[#0a1628]" />
        <Suspense fallback={
          <div className="absolute inset-0 bg-gradient-to-br from-[#020a18] via-[#051225] to-[#0a1628]" />
        }>
          <GlobeAnimation />
        </Suspense>
        {/* Blurred floating mini cards */}
        <div className="absolute inset-0 blur-[0.8px] opacity-40">
          <Suspense fallback={null}>
            <FloatingCards />
          </Suspense>
        </div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/50" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <h2 className="font-heading font-bold text-2xl lg:text-3xl text-white">
                Trending Connections
              </h2>
            </div>
            <p className="text-sm text-gray-400">
              Real-time executive positions, investments & corporate relationships
            </p>
          </div>
          <button
            onClick={() => navigate('/shareholders')}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            View Network
            <Icon name="ArrowRight" size={14} />
          </button>
        </div>

        {/* Feed Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {connections.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-3 p-3.5 rounded-lg border border-cyan-400/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-cyan-400/40 transition-all duration-200 cursor-pointer group shadow-[0_0_12px_rgba(56,189,248,0.15)] hover:shadow-[0_0_20px_rgba(56,189,248,0.3)]"
              onClick={() => navigate(`/individual-profile?id=${item.personId}`)}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border ${colorMap[item.color]}`}>
                <Icon name={item.icon} size={16} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="text-sm leading-snug">
                  <span className="font-semibold text-white group-hover:text-cyan-300 transition-colors">
                    {item.personName}
                  </span>
                  <span className="text-gray-500 mx-1">→</span>
                  <span
                    className="font-medium text-gray-300 hover:text-cyan-400 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/company-profile?id=${item.companyId}`);
                    }}
                  >
                    {item.companyName}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500">
                    {item.detail}
                  </span>
                  {item.industry && (
                    <>
                      <span className="text-gray-600">·</span>
                      <span className="text-xs text-gray-500">{item.industry}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrendingConnectionsFeed;
